import { AlertTriangle, Layers, MapPin, Mountain } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import type { GeoMetric, GeoStateRank } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { EmptyState } from "@/components/EmptyState";
import { GeoControls } from "@/components/geo/GeoControls";
import { type BasemapStyle, GeoMap } from "@/components/geo/GeoMap";
import { GeoStatesPanel } from "@/components/geo/GeoStatesPanel";
import { InsightCallout, type InsightStat } from "@/components/InsightCallout";
import { NotesList } from "@/components/NotesList";
import { PageHeader } from "@/components/PageHeader";
import { SkeletonLines } from "@/components/Skeleton";
import { useGeoHex, useGeoMetrics, useGeoStates } from "@/hooks/queries";
import { formatInt, formatScore } from "@/lib/format";
import type { HeightScale } from "@/lib/scale";

// Geo 3D Explorer — a dark, interactive fleet "command map". Real GPS hex cells rendered as
// extruded deck.gl columns over a CARTO dark-matter basemap; height + color encode the chosen
// metric. A non-technical exec can pick "GPS inaccuracy" and instantly see the hotbeds in 3D.

const DEFAULT_PEAK_M = 160000;

// "Low accuracy" share above this (%) is worth flagging in the insight stat row.
const LOW_ACC_FLAG_PCT = 25;

interface GeoInsight {
  readonly headline: string;
  readonly stats: readonly InsightStat[];
}

// Compute the "so what" from the carrier-region ranking: which region carries the worst GPS
// inaccuracy and where low-accuracy pings concentrate. Geo is NEUTRAL (never RAG) — these are
// quality observations, not a fleet-health verdict. Returns null until rows load.
const buildGeoInsight = (rows: readonly GeoStateRank[]): GeoInsight | null => {
  if (rows.length === 0) {
    return null;
  }
  const withInacc = rows.filter((r) => r.avgInaccuracyMeters !== null);
  const worstInacc = withInacc.reduce<GeoStateRank | null>(
    (worst, r) =>
      worst === null || (r.avgInaccuracyMeters ?? 0) > (worst.avgInaccuracyMeters ?? 0) ? r : worst,
    null,
  );
  const worstLowAcc = rows.reduce<GeoStateRank>(
    (worst, r) => (r.lowAccuracyPct > worst.lowAccuracyPct ? r : worst),
    rows[0],
  );
  const totalPings = rows.reduce((sum, r) => sum + r.sessionCount, 0);

  const lead =
    worstInacc !== null
      ? `Worst carrier-region for GPS accuracy: ${worstInacc.state} (avg ${formatScore(worstInacc.avgInaccuracyMeters, 0)}m inaccuracy)`
      : `${rows.length} carrier-regions reporting`;
  const tail =
    worstLowAcc.lowAccuracyPct >= LOW_ACC_FLAG_PCT
      ? `; ${formatScore(worstLowAcc.lowAccuracyPct, 0)}% of ${worstLowAcc.state} pings are low-accuracy.`
      : ".";

  const stats: InsightStat[] = [
    { label: "carrier-regions", value: formatInt(rows.length) },
    { label: "total pings", value: formatInt(totalPings) },
  ];
  if (worstInacc !== null) {
    stats.push({
      label: `worst avg inacc. (${worstInacc.state})`,
      value: `${formatScore(worstInacc.avgInaccuracyMeters, 0)}m`,
    });
  }
  return { headline: `${lead}${tail}`, stats };
};

// Use the matched-prefers-reduced-motion query so we don't auto-rotate / over-animate.
const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent): void => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
};

export const GeoPage = (): ReactNode => {
  const metricsQuery = useGeoMetrics();
  const statesQuery = useGeoStates();
  const reducedMotion = usePrefersReducedMotion();

  const metrics = metricsQuery.data?.data.metrics ?? [];
  const [metricKey, setMetricKey] = useState<string>("");
  const [heightScale, setHeightScale] = useState<HeightScale>("sqrt");
  const [peakHeightM, setPeakHeightM] = useState<number>(DEFAULT_PEAK_M);
  const [focusedState, setFocusedState] = useState<string | null>(null);
  const [basemap, setBasemap] = useState<BasemapStyle>("dark");

  // Default to the first metric once the catalog loads.
  useEffect(() => {
    if (metricKey === "" && metrics.length > 0) {
      setMetricKey(metrics[0].key);
    }
  }, [metricKey, metrics]);

  const activeMetric: GeoMetric | undefined = useMemo(
    () => metrics.find((m) => m.key === metricKey),
    [metrics, metricKey],
  );

  const hexQuery = useGeoHex(metricKey, metricKey.length > 0);
  const hexPayload = hexQuery.data?.data;
  const cells = hexPayload?.cells ?? [];

  // Higher-is-worse metrics get the hot inferno ramp; neutral metrics get viridis.
  const ramp = activeMetric?.higherIsWorse ? "inferno" : "viridis";

  const { minValue, maxValue, totalPings } = useMemo(() => {
    let lo = Number.POSITIVE_INFINITY;
    let hi = Number.NEGATIVE_INFINITY;
    let pings = 0;
    for (const c of cells) {
      const v = c.value ?? 0;
      if (v < lo) {
        lo = v;
      }
      if (v > hi) {
        hi = v;
      }
      pings += c.count;
    }
    if (!Number.isFinite(lo)) {
      lo = 0;
    }
    if (!Number.isFinite(hi)) {
      hi = 0;
    }
    return { minValue: lo, maxValue: hi, totalPings: pings };
  }, [cells]);

  const meta = hexQuery.data?.meta ?? metricsQuery.data?.meta;

  const stateRows = statesQuery.data?.data.rows ?? [];
  const insight = useMemo(() => buildGeoInsight(stateRows), [stateRows]);

  if (metricsQuery.isError) {
    return (
      <>
        <PageHeader title="Geo Explorer" />
        <ChartCard title="Geo">
          <EmptyState
            title="Couldn't load geo metrics"
            message={
              metricsQuery.error instanceof Error ? metricsQuery.error.message : "Unknown error"
            }
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Geo Explorer"
        description="Where the fleet actually drives — real device GPS, binned into 3D columns. Height and color encode the chosen metric. Drag to pan, right-drag to orbit, scroll to zoom."
        meta={meta}
      />

      {insight ? (
        <InsightCallout
          headline={insight.headline}
          stats={insight.stats}
          severity="neutral"
          cta={{ label: "Open Data Sources", to: "/data-sources" }}
        />
      ) : null}

      <div className="geo-kpis">
        <span className="geo-kpi">
          <Layers size={14} aria-hidden />
          <span className="geo-kpi-value br-tabular-nums">{formatInt(cells.length)}</span>
          <span className="geo-kpi-label">cells</span>
        </span>
        <span className="geo-kpi">
          <MapPin size={14} aria-hidden />
          <span className="geo-kpi-value br-tabular-nums">{formatInt(totalPings)}</span>
          <span className="geo-kpi-label">pings</span>
        </span>
        <span className="geo-kpi">
          <Mountain size={14} aria-hidden />
          <span className="geo-kpi-value br-tabular-nums">{formatInt(maxValue)}</span>
          <span className="geo-kpi-label">
            peak {activeMetric?.unit ? activeMetric.unit : "value"}
          </span>
        </span>
      </div>

      <div className="geo-stage">
        {hexQuery.isError ? (
          <EmptyState
            title="Couldn't load hex cells"
            message={hexQuery.error instanceof Error ? hexQuery.error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        ) : metricsQuery.isLoading || (hexQuery.isLoading && cells.length === 0) ? (
          <div className="geo-stage-loading">
            <SkeletonLines count={2} />
            <p>Loading map…</p>
          </div>
        ) : cells.length === 0 ? (
          <EmptyState
            title="No geo cells"
            message="This metric returned no cells for the window."
          />
        ) : (
          <>
            <GeoMap
              cells={cells}
              minValue={minValue}
              maxValue={maxValue}
              heightScale={heightScale}
              peakHeightM={peakHeightM}
              ramp={ramp}
              unit={activeMetric?.unit ?? ""}
              reducedMotion={reducedMotion}
              basemap={basemap}
            />
            <GeoControls
              metrics={metrics}
              activeMetric={activeMetric}
              onMetricChange={setMetricKey}
              heightScale={heightScale}
              onHeightScaleChange={setHeightScale}
              peakHeightM={peakHeightM}
              onPeakHeightChange={setPeakHeightM}
              ramp={ramp}
              minValue={minValue}
              maxValue={maxValue}
              basemap={basemap}
              onBasemapChange={setBasemap}
            />
          </>
        )}
      </div>

      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <ChartCard
          title="Per-state ranking"
          subtitle={
            focusedState
              ? `Focused on ${focusedState} — click again to clear · carrier-region attribution`
              : "Sortable — click a row to focus a carrier-region"
          }
          source="geo-states"
          loading={statesQuery.isLoading}
        >
          <GeoStatesPanel
            rows={stateRows}
            loading={statesQuery.isLoading}
            selectedState={focusedState}
            onSelectState={setFocusedState}
          />
        </ChartCard>

        {meta ? (
          <ChartCard title="Data notes" source="geo">
            <NotesList notes={meta.notes} />
          </ChartCard>
        ) : null}
      </div>
    </>
  );
};
